import { SubscriptionTier } from '../types'

// ---------------------------------------------------------------------------
// Feature definitions
// ---------------------------------------------------------------------------

export type Feature =
  // Quick Analysis tools — all middle+ (free sees the raw chart only)
  | 'quick_analysis_rpm'           // middle+
  | 'quick_analysis_wheel_speed'   // middle+
  | 'quick_analysis_egt'           // middle+
  | 'quick_analysis_boost'         // middle+
  | 'quick_analysis_speed'         // middle+
  | 'quick_analysis_turbo_speed'   // middle+
  | 'quick_analysis_oil_pressure'  // middle+
  | 'quick_analysis_peak'          // middle+
  // Weight & Balance
  | 'weight_balance'               // middle+
  | 'weight_balance_compare'       // middle+
  // Media
  | 'video_replay'                 // middle+ (widened from high-only 2026-09-30)
  | 'video_upload'                 // middle+
  | 'video_link'                   // free+
  | 'photo_upload'                 // free+
  // Community
  | 'pull_sharing'                 // middle+
  | 'forum_write'                  // middle+ (free is read-only in the forum)

// ---------------------------------------------------------------------------
// Tier → feature map
// ---------------------------------------------------------------------------

// Free is a view-only tier: upload photos + video links and read the forum,
// but no analysis presets, no posting, no video upload, no sharing.
const FREE_FEATURES: Feature[] = [
  'video_link',
  'photo_upload',
]

const MIDDLE_FEATURES: Feature[] = [
  ...FREE_FEATURES,
  'quick_analysis_rpm',
  'quick_analysis_wheel_speed',
  'quick_analysis_egt',
  'quick_analysis_boost',
  'quick_analysis_speed',
  'quick_analysis_turbo_speed',
  'quick_analysis_oil_pressure',
  'quick_analysis_peak',
  'weight_balance',
  'weight_balance_compare',
  'video_upload',
  'video_replay',
  'pull_sharing',
  'forum_write',
]

// The top tier is raised caps over middle; no feature is high-only today.
// Replay was high-only at first (docs/roadmap/VIDEO-REPLAY-PLAN.md §10) and
// was opened to middle on 2026-09-30.
const HIGH_FEATURES: Feature[] = [
  ...MIDDLE_FEATURES,
]

const TIER_FEATURES: Record<SubscriptionTier, Feature[]> = {
  free:   FREE_FEATURES,
  middle: MIDDLE_FEATURES,
  high:   HIGH_FEATURES,
}

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/** Returns true if the given tier includes access to the requested feature. */
export function canAccess(tier: SubscriptionTier, feature: Feature): boolean {
  return TIER_FEATURES[tier].includes(feature)
}

/**
 * Maximum number of pulls that can be compared simultaneously.
 * Returns Infinity for unlimited.
 */
export function getMaxComparisons(tier: SubscriptionTier): number {
  switch (tier) {
    case 'free':   return 1
    case 'middle': return 2
    case 'high':   return Infinity
  }
}

/**
 * Maximum number of tractors a user can save.
 * Returns null for unlimited.
 */
export function getMaxTractors(tier: SubscriptionTier): number | null {
  switch (tier) {
    case 'free':   return 1
    case 'middle': return 2
    case 'high':   return null
  }
}

/**
 * Storage limit in bytes.
 * Returns null for unlimited.
 */
export function getStorageLimitBytes(tier: SubscriptionTier): number | null {
  switch (tier) {
    case 'free':   return 2  * 1024 * 1024 * 1024  // 2 GB
    case 'middle': return 5  * 1024 * 1024 * 1024  // 5 GB
    case 'high':   return null
  }
}

/** Human-readable storage limit string. */
export function getStorageLimitLabel(tier: SubscriptionTier): string {
  switch (tier) {
    case 'free':   return '2 GB'
    case 'middle': return '5 GB'
    case 'high':   return 'Unlimited'
  }
}

/**
 * Human-readable tier display name — the single source of truth for what
 * users see. The underlying keys ('free'/'middle'/'high') are fixed
 * identifiers used by the DB, the Stripe webhook's PRICE_TO_TIER map, and
 * permission checks; only these display strings should change on a rename.
 */
export function getTierLabel(tier: SubscriptionTier): string {
  switch (tier) {
    case 'free':   return 'Pitstop Basic'
    case 'middle': return 'Pitstop Limited Pro'
    case 'high':   return 'Pitstop Pro'
  }
}

/**
 * Returns the minimum tier required to access a feature.
 * Useful for upgrade prompts ("Upgrade to Middle to unlock this").
 */
export function getRequiredTier(feature: Feature): SubscriptionTier {
  if (FREE_FEATURES.includes(feature))   return 'free'
  if (MIDDLE_FEATURES.includes(feature)) return 'middle'
  return 'high'
}
